import React from 'react';
import {Button, ButtonProps, Link, LinkProps} from 'react-aria-components';
import {SvgIcon} from '@montana/shared/svg-icon';
import cx from 'clsx';
import './nav-item.styles.scss';
import {IImage} from "@montana/models/generated/cms.interface";
import {Media, MontanaMediaRatios} from "@montana/shared/media";

interface INavItemProps {
    textSize?: 'small' | 'medium' | 'large';
    textColor?: 'default' | 'dimmed';
    iconName?: string;
    iconPosition?: 'left' | 'right';
    iconSize?: 'small' | 'medium' | 'large';
    isActive?: boolean;
    showBorder?: boolean;
    as?: 'button' | 'link';
    flexWidth?: boolean;
    verticalPadding?: 'none' | 'small' | 'medium';
    underlined?: boolean;
    image?: IImage;
}

export type LinkNavItemProps = LinkProps & INavItemProps & { hasSubMenu?: false };
export type ButtonNavItemProps = ButtonProps & Omit<INavItemProps, 'underlined'> & { hasSubMenu: true };

export const NavItem = React.forwardRef<HTMLAnchorElement | HTMLButtonElement, LinkNavItemProps | ButtonNavItemProps>((props, ref) => {
    const {
        textColor = 'default',
        textSize = 'medium',
        hasSubMenu,
        iconName,
        iconPosition = 'right',
        isActive,
        showBorder = true,
        flexWidth = false,
        children,
        as = 'link',
        verticalPadding = 'medium',
        image,
        ...rest
    } = props;

    const {underlined} = props as LinkNavItemProps;

    const renderChildren = (
        <>
            {iconName && iconPosition === 'left' && (
                <SvgIcon iconName={iconName} className="nav-item__icon nav-item__icon--left"/>
            )}
            {image && image.url && (
                <Media image={image} mediaRatio={MontanaMediaRatios.Square} animate={false} eager={false}
                       sizes="(min-width: 320px) 32px" srcSetWidth={[32]}/>
            )}
            <span
                className={cx('nav-item__text', `nav-item--text-${props.textSize}`, `nav-item--text-color-${props.textColor}`, {'nav-item--underlined': (as === 'link' && underlined)})}>{children}</span>
            {iconName && iconPosition === 'right' && (
                <SvgIcon iconName={iconName} className="nav-item__icon nav-item__icon--right"/>
            )}
        </>
    );

    const mergedClassName = (renderProps: any) =>
        cx(
            'nav-item',
            {
                [`nav-item--text-${renderProps.textSize}`]: renderProps.textSize,
                'nav-item--active': isActive,
                'nav-item--pressed': renderProps.isPressed,
                'nav-item--hovered': renderProps.isHovered,
                'nav-item--focused': renderProps.isFocusVisible,
                'nav-item--disabled': renderProps.isDisabled,
                'nav-item--has-border': showBorder,
                'nav-item--button': as === 'button',
                'nav-item--link': as === 'link',
                'nav-item--flex-width': flexWidth,
                [`nav-item--vertical-padding-${verticalPadding}`]: verticalPadding,
                'nav-item--image': image && image.url,
            }
        );

    if (as === 'button') {
        return (
            <Button {...(rest as ButtonProps)} className={mergedClassName} ref={ref as React.Ref<HTMLButtonElement>}>
                {renderChildren}
            </Button>
        );
    }

    return (
        <Link {...(rest as LinkProps)} className={mergedClassName} ref={ref as React.Ref<HTMLAnchorElement>}>
            {renderChildren}
        </Link>
    );
});

NavItem.displayName = 'NavItem';
